/**
 * Shared canvas node chrome — the card shell and connection ports used by the workflow canvas
 * (InlineNodeView) and the Teams canvas. Extracted so both surfaces render visually identical
 * nodes from one source of truth rather than re-deriving the styles per canvas.
 *
 * - `CanvasNodeCard`: the bordered, rounded paper card. `flowAccent` tints the border and
 *   `isSelected` adds persistent click-selection emphasis.
 * - `NodePort`: a ReactFlow `Handle` styled as the standard port dot. Drive it with `side`
 *   ('left' | 'right' | 'top' | 'bottom') and optional offsets — all filtered from the DOM.
 */

import { Handle } from 'reactflow';
import { styled, alpha } from '@mui/material/styles';
import { Box } from '@mui/material';

export const CanvasNodeCard = styled(Box, {
  shouldForwardProp: prop =>
    prop !== 'flowAccent' && prop !== 'cardWidth' && prop !== 'isSelected',
})(({ theme, flowAccent, cardWidth, isSelected }) => ({
  width: cardWidth || 520,
  borderRadius: 8,
  backgroundColor: theme.palette.background.paper,
  border: `${isSelected ? 2 : 1}px solid ${
    isSelected
      ? theme.palette.primary.main
      : flowAccent
        ? alpha(flowAccent, theme.palette.mode === 'dark' ? 0.48 : 0.34)
        : theme.palette.divider
  }`,
  boxShadow: isSelected
    ? `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}, 0 1px 3px rgba(0,0,0,0.08)`
    : '0 1px 3px rgba(0,0,0,0.08)',
  boxSizing: 'border-box',
  overflow: 'visible',
  position: 'relative',
}));

const portHoverTransform = (side) => {
  if (side === 'left') return 'translate(-50%, -50%)';
  if (side === 'right') return 'translate(50%, -50%)';
  if (side === 'top') return 'translate(-50%, -50%)';
  return 'translate(-50%, 50%)';
};

const portStyles = (theme, side, topOffset, crossOffset) => {
  const isHorizontal = side === 'top' || side === 'bottom';
  const base = {
    position: 'absolute',
    width: 14,
    height: 14,
    backgroundColor: theme.palette.text.secondary,
    border: `2px solid ${theme.palette.background.paper}`,
    borderRadius: '50%',
    zIndex: 10,
    cursor: 'crosshair',
    transition: 'transform 0.1s, background-color 0.1s',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: -14,
      left: -14,
      right: -14,
      bottom: -14,
      borderRadius: '50%',
    },
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      width: 20,
      height: 20,
      transform: portHoverTransform(side),
      transition: 'all 0.1s',
    },
  };

  if (isHorizontal) {
    return {
      '&&': {
        ...base,
        [side]: -7,
        left: crossOffset,
        transform: portHoverTransform(side),
      },
    };
  }

  return {
    '&&': {
      ...base,
      [side]: -7,
      top: topOffset,
      transform: portHoverTransform(side),
    },
  };
};

export const NodePort = styled(Handle, {
  shouldForwardProp: prop =>
    prop !== 'side' &&
    prop !== 'topOffset' &&
    prop !== 'crossOffset' &&
    prop !== 'isConnectable' &&
    prop !== 'visuallyHidden',
})(({
  theme,
  side = 'left',
  topOffset = 20,
  crossOffset = 260,
  isConnectable = true,
  visuallyHidden = false,
}) => ({
  ...portStyles(theme, side, topOffset, crossOffset),
  '&&': {
    ...portStyles(theme, side, topOffset, crossOffset)['&&'],
    cursor: isConnectable && !visuallyHidden ? 'crosshair' : 'default',
    ...(visuallyHidden
      ? {
          opacity: 0,
          width: 1,
          height: 1,
          minWidth: 0,
          minHeight: 0,
          border: 'none',
          backgroundColor: 'transparent',
          pointerEvents: 'none',
          '&::after': { display: 'none' },
          '&:hover': {
            opacity: 0,
            width: 1,
            height: 1,
            backgroundColor: 'transparent',
            transform: portHoverTransform(side),
          },
        }
      : {}),
  },
}));
